/**
 * useDraggablePreview — deixa a prévia da própria câmera ser arrastada para qualquer ponto do
 * palco da chamada (mouse, toque e setas do teclado). Começa no canto inferior direito.
 *
 * A posição é guardada como fração (0..1) do espaço livre em cada eixo, não em pixels: ao ampliar
 * a janela ou entrar em tela cheia a prévia continua no mesmo canto relativo, sem medir de novo.
 */
import { useRef, useState } from 'react';

/** Distância mínima entre a prévia e a borda do palco (igual ao antigo `bottom-2 right-2`). */
export const PREVIEW_MARGIN_PX = 8;
const KEY_STEP = 0.1;
const KEY_DELTAS = {
    ArrowLeft: [-KEY_STEP, 0],
    ArrowRight: [KEY_STEP, 0],
    ArrowUp: [0, -KEY_STEP],
    ArrowDown: [0, KEY_STEP],
};

const clamp01 = (n) => Math.min(1, Math.max(0, n));

/**
 * Nova fração depois de arrastar `delta` px num eixo com `travel` px de espaço livre.
 * Sem espaço livre (prévia do tamanho do palco) a posição não muda.
 */
export function dragRatio(origin, delta, travel) {
    return travel > 0 ? clamp01(origin + delta / travel) : origin;
}

/** Estilo absoluto da prévia: `x = 0` encosta na esquerda e `x = 1` na direita (idem `y`). */
export function previewStyle({ x, y }) {
    const inset = 2 * PREVIEW_MARGIN_PX;
    return {
        left: `calc(${PREVIEW_MARGIN_PX}px + ${x} * (100% - ${inset}px))`,
        top: `calc(${PREVIEW_MARGIN_PX}px + ${y} * (100% - ${inset}px))`,
        // O translate em % é do próprio tamanho da prévia: desconta a largura/altura dela do trajeto.
        transform: `translate(${-x * 100}%, ${-y * 100}%)`,
    };
}

/** Canto inicial: embaixo à direita. */
export const DEFAULT_PREVIEW_POSITION = Object.freeze({ x: 1, y: 1 });

/**
 * @param {React.RefObject<HTMLElement>} boundsRef — o palco que limita o arraste.
 * @param {{ x: number, y: number }} [initial] posição inicial (frações de 0 a 1); padrão: embaixo à direita.
 * @returns {{ style: object, dragging: boolean, handlers: object }}
 */
export function useDraggablePreview(boundsRef, initial = DEFAULT_PREVIEW_POSITION) {
    const [position, setPosition] = useState({ x: initial.x, y: initial.y });
    const [dragging, setDragging] = useState(false);
    const drag = useRef(null);

    const onPointerDown = (event) => {
        if (event.button !== 0 || !boundsRef.current) return;
        const bounds = boundsRef.current.getBoundingClientRect();
        const self = event.currentTarget.getBoundingClientRect();
        const inset = 2 * PREVIEW_MARGIN_PX;
        drag.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            origin: position,
            travelX: bounds.width - self.width - inset,
            travelY: bounds.height - self.height - inset,
        };
        event.currentTarget.setPointerCapture?.(event.pointerId);
        setDragging(true);
    };

    const onPointerMove = (event) => {
        const d = drag.current;
        if (!d || event.pointerId !== d.pointerId) return;
        setPosition({
            x: dragRatio(d.origin.x, event.clientX - d.startX, d.travelX),
            y: dragRatio(d.origin.y, event.clientY - d.startY, d.travelY),
        });
    };

    const endDrag = (event) => {
        if (!drag.current || event.pointerId !== drag.current.pointerId) return;
        event.currentTarget.releasePointerCapture?.(event.pointerId);
        drag.current = null;
        setDragging(false);
    };

    const onKeyDown = (event) => {
        const delta = KEY_DELTAS[event.key];
        if (!delta) return;
        event.preventDefault();
        // Arredonda para os passos não acumularem erro de ponto flutuante (0.1 * 10 ≠ 1).
        const step = (from, d) => clamp01(Math.round((from + d) * 100) / 100);
        setPosition((p) => ({ x: step(p.x, delta[0]), y: step(p.y, delta[1]) }));
    };

    return {
        style: previewStyle(position),
        dragging,
        handlers: { onPointerDown, onPointerMove, onPointerUp: endDrag, onPointerCancel: endDrag, onKeyDown },
    };
}
